import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Building2, MapPin, Clock } from 'lucide-react';
import { EventCard } from '../components/events/EventCard';
import { SEOHead } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImage = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

export const CollectionPage = () => {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchCollection = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await axios.get(`${API}/collections/${slug}`);
        setCollection(res.data.collection);
        setEvents(res.data.events);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchCollection();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="min-h-screen pt-24" data-testid="collection-not-found">
        <div className="section-container py-20 text-center">
          <h1 className="font-['Playfair_Display'] text-4xl text-white mb-4">Colección no encontrada</h1>
          <Link to="/catalogo" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft size={16} /> Volver al catálogo
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="collection-page">
      <SEOHead
        title={collection.name}
        description={collection.description}
        keywords={`${collection.name}, eventos culturales, ópera, ballet, conciertos`}
        canonicalUrl={`/coleccion/${collection.slug}`}
      />

      {/* Hero Section — same style as EventDetail */}
      <section className="relative" data-testid="collection-hero">
        {collection.image_url ? (
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${resolveImage(collection.image_url)})` }} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--surface)] to-[var(--background)]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--background)] via-[var(--background)]/70 to-transparent" />

        <div className="relative z-10 section-container pt-32 pb-20 min-h-[70vh] flex flex-col justify-end">
          <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
            Descubre
          </span>
          <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl lg:text-7xl text-white mb-4" data-testid="collection-title">
            {collection.name}
          </h1>
          {collection.description && (
            <p className="text-white/70 text-lg md:text-xl max-w-3xl leading-relaxed" data-testid="collection-description">
              {collection.description}
            </p>
          )}
        </div>
      </section>

      {/* Events Grid */}
      <div className="section-container py-12">
        {events.length === 0 ? (
          <div className="text-center py-20 bg-[var(--surface)] border border-white/5" data-testid="collection-empty">
            <Calendar size={48} className="text-white/10 mx-auto mb-4" />
            <p className="text-white/50">No hay eventos en esta colección todavía</p>
          </div>
        ) : (
          <>
            <p className="text-white/40 text-sm uppercase tracking-widest mb-8">
              {events.length} {events.length === 1 ? 'evento' : 'eventos'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="collection-events-grid">
              {events.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CollectionPage;
